/*
 * VoxPontLegalPage — renders the VoxPont Terms of Use and Privacy Policy.
 *
 * Served at /voxpont-terms and /voxpont-privacy. Like VoxPontAuthPage and
 * VoxPontBetaJoinPage, this is a deliberate bespoke VoxPont brand surface
 * (navy + gold) with inline design tokens — not the HealthDesk MUI theme.
 *
 * Content lives in voxpontLegalContent.js. The `doc` prop ("terms" | "privacy")
 * selects which document to render; defaults to "terms".
 */
import React from "react";
import { Link } from "react-router-dom";
import VoxPontLogo from "components/voxpont/VoxPontLogo";
import {
  VOXPONT_LEGAL_DOCS,
  VOXPONT_LEGAL_ENTITY,
} from "./voxpontLegalContent";

const STYLES = `
.vplegal-root {
  --navy-deep: #0C1E36;
  --navy: #122B4D;
  --gold-antique: #b08a28;
  --gold-polished: #d4af37;
  --gold-champagne: #c8a23a;
  --gold-metal: linear-gradient(105deg,#7a5e17 0%,#8c6a1a 16%,#b08a28 34%,#d4af37 50%,#b08a28 66%,#8c6a1a 84%,#7a5e17 100%);
  min-height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  background: var(--navy-deep);
  padding: 32px 24px 64px;
  box-sizing: border-box;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}
.vplegal-card {
  position: relative;
  width: 100%;
  max-width: 760px;
  background: var(--navy);
  border: 1px solid rgba(196,154,44,0.18);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.45);
}
.vplegal-goldbar { height: 3px; width: 100%; background: var(--gold-metal); }
.vplegal-body { position: relative; padding: 36px 40px 44px; }
@media (max-width: 600px) { .vplegal-body { padding: 28px 22px 36px; } }
.vplegal-header { text-align: center; margin-bottom: 28px; }
.vplegal-wordmark {
  font-family: 'Federo', serif;
  font-size: 1.6rem;
  line-height: 1.1;
  margin: 12px 0 0;
  background: var(--gold-metal);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--gold-polished);
}
.vplegal-subline {
  margin: 8px 0 0;
  text-transform: uppercase;
  color: var(--gold-antique);
  font-size: 0.62rem;
  letter-spacing: 0.2em;
}
.vplegal-title {
  margin: 24px 0 4px;
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
}
.vplegal-effective {
  margin: 0 0 4px;
  text-align: center;
  color: rgba(255,255,255,0.45);
  font-size: 0.8rem;
}
.vplegal-entity {
  margin: 0 0 24px;
  text-align: center;
  color: var(--gold-champagne);
  font-size: 0.85rem;
}
.vplegal-intro p {
  color: rgba(255,255,255,0.78);
  font-size: 0.92rem;
  line-height: 1.65;
  margin: 0 0 14px;
}
.vplegal-section { margin-top: 26px; }
.vplegal-section h2 {
  color: var(--gold-polished);
  font-size: 1.02rem;
  font-weight: 700;
  margin: 0 0 10px;
}
.vplegal-section p {
  color: rgba(255,255,255,0.72);
  font-size: 0.9rem;
  line-height: 1.65;
  margin: 0 0 12px;
}
.vplegal-section ul {
  margin: 0 0 12px;
  padding-left: 20px;
}
.vplegal-section li {
  color: rgba(255,255,255,0.72);
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0 0 8px;
}
.vplegal-footer {
  margin-top: 36px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  justify-content: center;
}
.vplegal-link {
  background: none;
  border: none;
  color: var(--gold-polished);
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}
.vplegal-link:hover { text-decoration: underline; }
`;

const DOC_LINKS = {
  terms: { label: "Terms of Use", path: "/voxpont-terms" },
  privacy: { label: "Privacy Policy", path: "/voxpont-privacy" },
  baa: { label: "Business Associate Agreement", path: "/voxpont-baa" },
};

function renderBodyEntry(entry, index) {
  if (entry && typeof entry === "object" && Array.isArray(entry.list)) {
    return (
      <ul key={index}>
        {entry.list.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  return <p key={index}>{entry}</p>;
}

export default function VoxPontLegalPage({ doc = "terms" }) {
  const activeKey = VOXPONT_LEGAL_DOCS[doc] ? doc : "terms";
  const document = VOXPONT_LEGAL_DOCS[activeKey];
  const otherLinks = Object.entries(DOC_LINKS).filter(
    ([key]) => key !== activeKey
  );

  return (
    <div className="vplegal-root">
      <style>{STYLES}</style>
      <div className="vplegal-card">
        <div className="vplegal-goldbar" />
        <div className="vplegal-body">
          <div className="vplegal-header">
            <VoxPontLogo
              variant="symbol"
              tone="onDark"
              height={48}
              title="VoxPont"
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                margin: "0 auto",
              }}
            />
            <h1 className="vplegal-wordmark">VoxPont</h1>
            <p className="vplegal-subline">VOX PONT — Voice across languages</p>
          </div>

          <h2 className="vplegal-title">{document.title}</h2>
          <p className="vplegal-effective">
            Effective date: {document.effectiveDate}
          </p>
          <p className="vplegal-entity">
            Operated by {VOXPONT_LEGAL_ENTITY}
          </p>

          <div className="vplegal-intro">
            {document.intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {document.sections.map((section, i) => (
            <div className="vplegal-section" key={i}>
              <h2>{section.heading}</h2>
              {section.body.map((entry, j) => renderBodyEntry(entry, j))}
            </div>
          ))}

          <div className="vplegal-footer">
            {otherLinks.map(([key, link]) => (
              <Link className="vplegal-link" to={link.path} key={key}>
                {link.label}
              </Link>
            ))}
            <Link className="vplegal-link" to="/voxpont-auth">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
